import React from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Typography,
  IconButton,
  Divider,
} from '@mui/material';
import {
  LinkedIn as LinkedInIcon,
  Twitter as TwitterIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import Logo from './Logo';

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    product: [
      { label: 'Features', path: '/features' },
      { label: 'Security', path: '/features#security' },
      { label: 'Compliance', path: '/compliance' },
      // Pre-launch: pricing hidden; drive to the live demo instead.
      { label: 'Try the Demo', path: '/request-demo' },
    ],
    company: [
      { label: 'About Us', path: '/about' },
      { label: 'Contact', path: '/contact' },
      { label: 'Request a Demo', path: '/request-demo' },
    ],
    legal: [
      { label: 'Privacy Policy', path: '/privacy' },
      { label: 'Terms of Service', path: '/terms' },
      { label: 'FDCPA Compliance', path: '/compliance#fdcpa' },
      { label: 'TCPA Compliance', path: '/compliance#tcpa' },
    ],
  };

  return (
    <Box
      component="footer"
      sx={{
        bgcolor: 'background.paper',
        borderTop: '1px solid',
        borderColor: 'divider',
        py: 6,
        mt: 'auto',
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          {/* Logo and Description */}
          <Grid item xs={12} md={4}>
            <Logo size="medium" />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, maxWidth: 300 }}>
              Professional debt collection software with FDCPA and TCPA compliance
              controls built in. Streamline your operations and collect more efficiently.
            </Typography>
            <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
              <IconButton
                size="small"
                sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
              >
                <LinkedInIcon />
              </IconButton>
              <IconButton
                size="small"
                sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
              >
                <TwitterIcon />
              </IconButton>
              <IconButton
                size="small"
                component="a"
                href="mailto:contact@debtornator.com"
                sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
              >
                <EmailIcon />
              </IconButton>
            </Box>
          </Grid>

          {/* Product Links */}
          <Grid item xs={6} sm={4} md={2}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
              Product
            </Typography>
            {footerLinks.product.map((link) => (
              <Typography
                key={link.path}
                component={Link}
                to={link.path}
                variant="body2"
                sx={{
                  display: 'block',
                  color: 'text.secondary',
                  textDecoration: 'none',
                  mb: 1,
                  '&:hover': { color: 'primary.main' },
                }}
              >
                {link.label}
              </Typography>
            ))}
          </Grid>

          {/* Company Links */}
          <Grid item xs={6} sm={4} md={2}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
              Company
            </Typography>
            {footerLinks.company.map((link) => (
              <Typography
                key={link.path}
                component={Link}
                to={link.path}
                variant="body2"
                sx={{
                  display: 'block',
                  color: 'text.secondary',
                  textDecoration: 'none',
                  mb: 1,
                  '&:hover': { color: 'primary.main' },
                }}
              >
                {link.label}
              </Typography>
            ))}
          </Grid>

          {/* Legal Links */}
          <Grid item xs={6} sm={4} md={2}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
              Legal
            </Typography>
            {footerLinks.legal.map((link) => (
              <Typography
                key={link.path}
                component={Link}
                to={link.path}
                variant="body2"
                sx={{
                  display: 'block',
                  color: 'text.secondary',
                  textDecoration: 'none',
                  mb: 1,
                  '&:hover': { color: 'primary.main' },
                }}
              >
                {link.label}
              </Typography>
            ))}
          </Grid>

          {/* Contact Info */}
          <Grid item xs={6} sm={4} md={2}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
              Contact
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Sales: sales@debtornator.com
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Support: support@debtornator.com
            </Typography>
            <Typography variant="body2" color="text.secondary">
              (719) 394-3146
            </Typography>
          </Grid>
        </Grid>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            &copy; {currentYear} Debtornator. All rights reserved.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            FDCPA & TCPA Compliant Software
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
